<template>
  <BaseLayout>
    <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center">
          <router-link
            to="/users"
            class="text-gray-500 hover:text-gray-700 mr-4"
          >
            <ArrowLeftIcon class="h-5 w-5" />
          </router-link>
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">Crear Usuario</h1>
            <p class="mt-1 text-sm text-gray-600">
              Complete la información para crear un nuevo usuario en el sistema.
            </p>
          </div>
        </div>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="space-y-8">
        <!-- Información Personal -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Información Personal
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Nombre *</label>
                <input
                  v-model="form.firstName"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.firstName }"
                  placeholder="Ingrese el nombre"
                />
                <p v-if="errors.firstName" class="form-error">{{ errors.firstName }}</p>
              </div>

              <div>
                <label class="form-label">Apellido *</label>
                <input
                  v-model="form.lastName"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.lastName }"
                  placeholder="Ingrese el apellido"
                />
                <p v-if="errors.lastName" class="form-error">{{ errors.lastName }}</p>
              </div>

              <div>
                <label class="form-label">DNI *</label>
                <input
                  v-model="form.dni"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.dni }"
                  placeholder="Número de documento"
                  @input="handleDniChange"
                />
                <p v-if="errors.dni" class="form-error">{{ errors.dni }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  El DNI se usará como contraseña por defecto.
                </p>
              </div>

              <div>
                <label class="form-label">Teléfono *</label>
                <input
                  v-model="form.telefono"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.telefono }"
                  placeholder="Número de teléfono"
                  @input="handleTelefonoChange"
                />
                <p v-if="errors.telefono" class="form-error">{{ errors.telefono }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  El teléfono se usará como nombre de usuario automáticamente.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Información de Cuenta -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Información de Cuenta
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Usuario *</label>
                <input
                  v-model="form.username"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.username }"
                  placeholder="Se genera desde el teléfono (editable)"
                  @input="onUsernameInput"
                />
                <p v-if="errors.username" class="form-error">{{ errors.username }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Se genera automáticamente desde el teléfono, pero puede modificarse.
                </p>
              </div>

              <div>
                <label class="form-label">Email</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="form-input"
                  :class="{ 'border-red-300': errors.email }"
                  placeholder="correo@ejemplo.com (opcional)"
                />
                <p v-if="errors.email" class="form-error">{{ errors.email }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Campo opcional. Puede dejarse vacío.
                </p>
              </div>

              <div>
                <label class="form-label">Contraseña *</label>
                <div class="relative">
                  <input
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    required
                    class="form-input pr-10"
                    :class="{ 'border-red-300': errors.password }"
                    placeholder="Se genera desde el DNI (editable)"
                    @input="onPasswordInput"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <EyeIcon v-if="!showPassword" class="h-5 w-5" />
                    <EyeSlashIcon v-else class="h-5 w-5" />
                  </button>
                </div>
                <p v-if="errors.password" class="form-error">{{ errors.password }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Se genera automáticamente desde el DNI, pero puede modificarse.
                </p>
              </div>

              <div>
                <label class="form-label">Confirmar Contraseña *</label>
                <div class="relative">
                  <input
                    v-model="form.confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    required
                    class="form-input pr-10"
                    :class="{ 'border-red-300': errors.confirmPassword }"
                    placeholder="Repita la contraseña"
                    @input="onPasswordInput"
                  />
                  <button
                    type="button"
                    @click="showConfirmPassword = !showConfirmPassword"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <EyeIcon v-if="!showConfirmPassword" class="h-5 w-5" />
                    <EyeSlashIcon v-else class="h-5 w-5" />
                  </button>
                </div>
                <p v-if="errors.confirmPassword" class="form-error">{{ errors.confirmPassword }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Rol y Permisos -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Rol y Permisos
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Rol del Usuario *</label>
                <select
                  v-model="form.role"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.role }"
                  @change="handleRoleChange"
                >
                  <option value="">Seleccione un rol</option>
                  <option value="admin">Administrador</option>
                  <option value="validador">Validador</option>
                  <option value="digitador">Digitador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="transmisor">Transmisor</option>
                  <option value="auditor">Auditor</option>
                </select>
                <p v-if="errors.role" class="form-error">{{ errors.role }}</p>
                <div class="mt-2 text-sm text-gray-600">
                  <div v-if="form.role" class="p-3 bg-blue-50 rounded-md">
                    <p class="font-medium text-blue-900">{{ getRoleDescription(form.role) }}</p>
                    <p class="text-blue-700 mt-1">{{ getRolePermissions(form.role) }}</p>
                  </div>
                </div>
              </div>

              <div v-if="form.role === 'transmisor' || form.role === 'auditor'" class="col-span-2">
                <label class="form-label">Establecimientos *</label>
                <input
                  v-model="establecimientosInput"
                  type="text"
                  :required="form.role === 'transmisor' || form.role === 'auditor'"
                  class="form-input"
                  :class="{ 'border-red-300': errors.establecimientosIds }"
                  placeholder="Ingrese IDs separados por comas o pipes (ej: 54, 55, 56 o 54|55|56)"
                  @input="handleEstablecimientosInput"
                  @keydown.enter.prevent="addEstablecimiento"
                />
                <p v-if="errors.establecimientosIds" class="form-error">{{ errors.establecimientosIds }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Puede ingresar múltiples IDs separados por comas (,) o pipes (|). Presione Enter para agregar.
                </p>
                
                <!-- Chips/Tags de establecimientos -->
                <div v-if="form.establecimientosIds.length > 0" class="flex flex-wrap gap-2 mt-3">
                  <span
                    v-for="(id, index) in form.establecimientosIds"
                    :key="index"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {{ id }}
                    <button
                      type="button"
                      @click="removeEstablecimiento(index)"
                      class="ml-2 inline-flex items-center justify-center w-4 h-4 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full"
                    >
                      ×
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3">
          <router-link
            to="/users"
            class="btn-secondary"
          >
            Cancelar
          </router-link>
          <button
            type="submit"
            :disabled="loading"
            class="btn-primary"
          >
            <span v-if="!loading">Crear Usuario</span>
            <span v-else class="flex items-center">
              <div class="loading-spinner mr-2"></div>
              Creando...
            </span>
          </button>
        </div>
      </form>
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { apiService } from '@/services/apiService'
import { useToast } from 'vue-toastification'
import BaseLayout from '@/components/BaseLayout.vue'
import { ArrowLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const toast = useToast()

const loading = ref(false)

const form = reactive({
  firstName: '',
  lastName: '',
  dni: '',
  telefono: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: '',
  establecimientoId: null as number | null, // Legacy - mantener por compatibilidad
  establecimientosIds: [] as number[]
})

const establecimientosInput = ref('')

const errors = reactive({
  firstName: '',
  lastName: '',
  dni: '',
  telefono: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: '',
  establecimientoId: '',
  establecimientosIds: ''
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const userEditedUsername = ref(false)
const userEditedPassword = ref(false)

function clearErrors() {
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })
}

function validateForm(): boolean {
  clearErrors()
  let isValid = true

  // Validar campos requeridos
  if (!form.firstName.trim()) {
    errors.firstName = 'El nombre es requerido'
    isValid = false
  }

  if (!form.lastName.trim()) {
    errors.lastName = 'El apellido es requerido'
    isValid = false
  }

  if (!form.dni.trim()) {
    errors.dni = 'El DNI es requerido'
    isValid = false
  } else if (form.dni.trim().length === 0) {
    errors.dni = 'El DNI no puede estar vacío'
    isValid = false
  }

  if (!form.telefono.trim()) {
    errors.telefono = 'El teléfono es requerido'
    isValid = false
  } else if (form.telefono.trim().length === 0) {
    errors.telefono = 'El teléfono no puede estar vacío'
    isValid = false
  }

  if (!form.username.trim()) {
    errors.username = 'Error generando el usuario desde el teléfono'
    isValid = false
  }

  // Email es opcional, pero si se proporciona debe ser válido
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'El email no es válido'
    isValid = false
  }

  if (!form.password) {
    errors.password = 'La contraseña es requerida'
    isValid = false
  } else if (form.password.length < 6) {
    errors.password = 'La contraseña debe tener al menos 6 caracteres'
    isValid = false
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = 'Confirme la contraseña'
    isValid = false
  } else if (form.password !== form.confirmPassword) {
    errors.confirmPassword = 'Las contraseñas no coinciden'
    isValid = false
  }

  if (!form.role) {
    errors.role = 'Seleccione un rol'
    isValid = false
  }

  if ((form.role === 'transmisor' || form.role === 'auditor') && form.establecimientosIds.length === 0) {
    errors.establecimientosIds = 'Debe ingresar al menos un establecimiento para transmisores y auditores'
    isValid = false
  }

  return isValid
}

function handleDniChange() {
  // Solo sugerir password si el usuario no ha editado manualmente
  if (!userEditedPassword.value && form.dni && form.dni.trim()) {
    form.password = form.dni.trim()
    form.confirmPassword = form.dni.trim()
  }
}

function handleTelefonoChange() {
  // Solo sugerir username si el usuario no ha editado manualmente
  if (!userEditedUsername.value && form.telefono && form.telefono.trim()) {
    form.username = form.telefono.trim()
  }
}

// Detectar cuando el usuario edita manualmente el username
function onUsernameInput() {
  userEditedUsername.value = true
}

// Detectar cuando el usuario edita manualmente la password
function onPasswordInput() {
  userEditedPassword.value = true
}

function handleRoleChange() {
  if (form.role !== 'transmisor' && form.role !== 'auditor') {
    form.establecimientoId = null
    form.establecimientosIds = []
    establecimientosInput.value = ''
  }
}

// Parsear IDs desde string (comas o pipes)
function parseEstablecimientosFromString(value: string): number[] {
  if (!value || !value.trim()) return []
  
  const ids = value
    .split(/[,|]/)
    .map(id => parseInt(id.trim()))
    .filter(id => !isNaN(id) && id > 0)
  
  return [...new Set(ids)] // Eliminar duplicados
}

// Manejar input de establecimientos
function handleEstablecimientosInput() {
  const parsed = parseEstablecimientosFromString(establecimientosInput.value)
  if (parsed.length > 0) {
    form.establecimientosIds = parsed
    errors.establecimientosIds = ''
  }
}

// Agregar establecimiento (al presionar Enter)
function addEstablecimiento() {
  handleEstablecimientosInput()
}

// Remover un establecimiento específico
function removeEstablecimiento(index: number) {
  form.establecimientosIds.splice(index, 1)
  
  // Actualizar el input para reflejar los IDs restantes
  if (form.establecimientosIds.length > 0) {
    establecimientosInput.value = form.establecimientosIds.join(', ')
  } else {
    establecimientosInput.value = ''
  }
}

function getRoleDescription(role: string): string {
  const descriptions: Record<string, string> = {
    admin: 'Administrador del Sistema',
    validador: 'Validador de Actas',
    digitador: 'Digitador de Datos',
    supervisor: 'Supervisor de Procesos',
    transmisor: 'Transmisor de Establecimiento',
    auditor: 'Auditor de Establecimiento'
  }
  return descriptions[role] || ''
}

function getRolePermissions(role: string): string {
  const permissions: Record<string, string> = {
    admin: 'Acceso completo al sistema de administración',
    validador: 'Validación de actas electorales',
    digitador: 'Digitación de datos de actas',
    supervisor: 'Supervisión de procesos electorales',
    transmisor: 'Transmisión de actas desde establecimiento',
    auditor: 'Auditoría de actas desde establecimiento'
  }
  return permissions[role] || ''
}

async function handleSubmit() {
  if (!validateForm()) {
    toast.error('Por favor corrija los errores en el formulario')
    return
  }

  try {
    loading.value = true

    // Asegurar que los establecimientos estén actualizados desde el input
    handleEstablecimientosInput()
    
    const userData = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      username: form.username.trim(),
      email: form.email.trim().toLowerCase() || undefined,
      password: form.password,
      role: form.role,
      dni: form.dni.trim() || undefined,
      telefono: form.telefono.trim() || undefined,
      establecimientosIds: form.establecimientosIds.length > 0 ? form.establecimientosIds : undefined
    }

    await apiService.createUser(userData)

    toast.success(`Usuario ${userData.username} creado exitosamente`)
    router.push('/users')

  } catch (error: any) {
    if (error.response?.status === 409) {
      const errorMessage = error.response.data.message || error.response.data.error || 'El usuario ya existe'
      
      if (errorMessage.includes('DNI')) {
        errors.dni = errorMessage
      } else if (errorMessage.includes('teléfono') || errorMessage.includes('telefono')) {
        errors.telefono = errorMessage
      } else if (errorMessage.includes('email')) {
        errors.email = errorMessage
      } else if (errorMessage.includes('username')) {
        errors.username = errorMessage
      } else if (errorMessage.includes('establecimiento')) {
        errors.establecimientosIds = errorMessage
      } else {
        toast.error(errorMessage)
      }
    } else {
      // Extraer el mensaje de error del backend
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message
      toast.error(errorMessage)
    }
  } finally {
    loading.value = false
  }
}

// Auto-sugerir password basado en DNI (solo si no ha sido editado manualmente)
watch(() => form.dni, (newDni) => {
  if (!userEditedPassword.value) {
    if (newDni && newDni.trim()) {
      form.password = newDni.trim()
      form.confirmPassword = newDni.trim()
    } else {
      form.password = ''
      form.confirmPassword = ''
    }
  }
})

// Auto-sugerir username basado en teléfono (solo si no ha sido editado manualmente)
watch(() => form.telefono, (newTelefono) => {
  if (!userEditedUsername.value) {
    if (newTelefono && newTelefono.trim()) {
      form.username = newTelefono.trim()
    } else {
      form.username = ''
    }
  }
})
</script>
